import { Popconfirm, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { TableRow as DetailsRow } from "@/views/DataDiscrepancies/Components";
import { linkRenderer, renderCell } from "@/utils/tableConfigs/GlobalFunctions";
import TrendLine from "@/views/Risk/shared/TrendLinePlot/TrendLine";
import {
  extractTrendData,
  calculateBandThresholds,
  SIGNAL_TREND_FIELDS,
} from "@/views/Risk/shared/TrendLinePlot/trendUtils";
import { useSignals } from "@/contexts/SignalsContext";
import SignalScoreCell from "./SignalScoreCell";
import type { SignalTableRow } from "./utils";

const { Title } = Typography;

type SignalsDrawerProps = {
  records: SignalTableRow[];
  selectedRecord: SignalTableRow | null;
  setSelectedRecord: (record: SignalTableRow | null) => void;
};

export default function SignalsDrawer({
  records,
  selectedRecord,
  setSelectedRecord,
}: SignalsDrawerProps) {
  const { removeSignal } = useSignals();

  const handleRemove = () => {
    if (!selectedRecord) return;
    removeSignal(String(selectedRecord.signal_id));
    setSelectedRecord(null);
  };
  const signalTrendData = selectedRecord
    ? extractTrendData(selectedRecord, SIGNAL_TREND_FIELDS)
    : null;
  // The current-score dial is gone, so the trajectory is the only plot left:
  // a record with a score but no history now has nothing to draw.
  const hasPlotData = (signalTrendData?.trendScores.length ?? 0) > 0;
  const signalBandThresholds = calculateBandThresholds(
    records,
    SIGNAL_TREND_FIELDS,
  );
  const headersArray = [
    {
      key: "SIGNAL ID",
      value: (
        <span className="text-white">{selectedRecord?.signal_id || "-"}</span>
      ),
    },
  ];

  return (
    <ResizableDrawer
      headerTitle="Signal Information"
      headersArray={headersArray}
      sizeToggle={{
        collapsedWidth: 654,
        expandedWidth: 1250,
        initialWidth: 654,
      }}
      placement="right"
      open={selectedRecord !== null}
      className="data-management-drawer"
      onClose={() => setSelectedRecord(null)}
      closeIcon={null}
      closable={false}
      headerActions={
        selectedRecord && (
          <Popconfirm
            title="Remove this signal?"
            description="This signal will be removed from the list."
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={handleRemove}
          >
            <button
              type="button"
              aria-label="Remove signal"
              className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 leading-none text-white opacity-70 hover:opacity-100"
            >
              <DeleteOutlined className="text-base" />
            </button>
          </Popconfirm>
        )
      }
    >
      {selectedRecord ? (
        <div className="flex flex-col gap-6">
          <section>
            <Title level={5}>Signal Overview</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Signal ID"
                    value={renderCell(selectedRecord.signal_id)}
                  />
                  <DetailsRow
                    label="Category"
                    value={renderCell(selectedRecord.category)}
                  />
                  <DetailsRow
                    label="Signal"
                    value={renderCell(selectedRecord.signal)}
                  />
                  <DetailsRow
                    label="Created By"
                    value={
                      <AvatarWithInitials
                        name={
                          typeof selectedRecord.created_by === "string" &&
                          selectedRecord.created_by.trim().length > 0
                            ? selectedRecord.created_by
                            : null
                        }
                        avatarSize={28}
                        labelFontSize={14}
                      />
                    }
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Quality Tolerance Limits</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Current Score"
                    value={
                      <SignalScoreCell
                        score={selectedRecord.current_score}
                        band={selectedRecord.band}
                      />
                    }
                  />
                  <DetailsRow
                    label="Breach Status"
                    value={
                      selectedRecord.breach_status ? (
                        <span style={{ color: "#D9534F" }}>Breached</span>
                      ) : (
                        <span style={{ color: "#53A87A" }}>On Track</span>
                      )
                    }
                  />
                  <DetailsRow
                    label="Low Threshold"
                    value={renderCell(selectedRecord.low_threshold)}
                  />
                  <DetailsRow
                    label="High Threshold"
                    value={renderCell(selectedRecord.high_threshold)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Signal Score Trend</Title>
            <div className="rounded-sm bg-white p-4">
              {hasPlotData && signalTrendData ? (
                <TrendLine
                  values={signalTrendData.trendScores}
                  statuses={signalTrendData.trendStatuses}
                  thresholds={signalBandThresholds}
                />
              ) : (
                <div className="flex min-h-[180px] items-center justify-center text-sm text-[#00000073]">
                  Gathering evidence
                </div>
              )}
            </div>
          </section>
          <section>
            <Title level={5}>Risk and Breach Relationships</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Linked Risks"
                    value={renderCell(
                      selectedRecord.linked_risks,
                      linkRenderer((id) => `../risks?openRisk=${id}`),
                    )}
                  />
                  <DetailsRow
                    label="Linked Breaches"
                    value={renderCell(
                      selectedRecord.linked_breaches,
                      linkRenderer((id) => `../issues?openIssue=${id}`),
                    )}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Ownership and Notes</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Owners"
                    value={renderCell(selectedRecord.owners)}
                  />
                  <DetailsRow
                    label="Assignee"
                    value={
                      <AvatarWithInitials
                        name={
                          typeof selectedRecord.assignee === "string" &&
                          selectedRecord.assignee.trim().length > 0
                            ? selectedRecord.assignee
                            : null
                        }
                        avatarSize={28}
                        labelFontSize={14}
                      />
                    }
                  />
                  <DetailsRow
                    label="Comments"
                    value={renderCell(selectedRecord.comments_governance_log)}
                  />
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}
    </ResizableDrawer>
  );
}
